<template>
  <div
    class="contact-item"
    :class="[
      {
        selected: isSelected || formattedItem.isActiveCall,
        disabled: disabled && !formattedItem.isActiveCall
      },
      inlineButtons ? 'flex-row justify-between' : 'flex-col'
    ]"
  >
    <div class="flex justify-between w-full">
      <div class="flex items-center gap-3">
        <Icon
          v-if="showIcon"
          :type="formattedItem.icon"
          class="text-md"
        />
        <div>
          <div class="font-medium text-[0.92rem]">{{ formattedItem.displayTitle }}</div>
          <div class="text-xs text-secondary -mt-[1px] flex items-center">
            <div class="flex items-center gap-1">
              <Icon
                type="circle"
                :class="[`text-${formattedItem.statusIconColor}`]"
                class="text-[6px]"
              />
              <div :class="[timerWarningClass]">{{ formattedItem.statusMessage }}</div>
            </div>
            <Icon type="pipe" />
            <div
              class="min-w-[28px]"
              :class="[timerWarningClass]"
            >
              {{ timerDisplayValue }}
            </div>
          </div>
        </div>
      </div>
      <div class="flex gap-3 items-center">
        <div
          v-if="formattedItem.status == 'in-progress' && showAudioWave"
          class="audio-wave"
        >
          <svg
            id="wave"
            data-name="Layer 1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 50 38.05"
          >
            <title>Audio Wave</title>
            <path
              id="Line_1"
              data-name="Line 1"
              d="M0.91,15L0.78,15A1,1,0,0,0,0,16v6a1,1,0,1,0,2,0s0,0,0,0V16a1,1,0,0,0-1-1H0.91Z"
            />
            <path
              id="Line_2"
              data-name="Line 2"
              d="M6.91,9L6.78,9A1,1,0,0,0,6,10V28a1,1,0,1,0,2,0s0,0,0,0V10A1,1,0,0,0,7,9H6.91Z"
            />
            <path
              id="Line_3"
              data-name="Line 3"
              d="M12.91,0L12.78,0A1,1,0,0,0,12,1V37a1,1,0,1,0,2,0s0,0,0,0V1a1,1,0,0,0-1-1H12.91Z"
            />
            <path
              id="Line_4"
              data-name="Line 4"
              d="M18.91,10l-0.12,0A1,1,0,0,0,18,11V27a1,1,0,1,0,2,0s0,0,0,0V11a1,1,0,0,0-1-1H18.91Z"
            />
            <path
              id="Line_5"
              data-name="Line 5"
              d="M24.91,15l-0.12,0A1,1,0,0,0,24,16v6a1,1,0,0,0,2,0s0,0,0,0V16a1,1,0,0,0-1-1H24.91Z"
            />
            <path
              id="Line_6"
              data-name="Line 6"
              d="M30.91,10l-0.12,0A1,1,0,0,0,30,11V27a1,1,0,1,0,2,0s0,0,0,0V11a1,1,0,0,0-1-1H30.91Z"
            />
            <path
              id="Line_7"
              data-name="Line 7"
              d="M36.91,0L36.78,0A1,1,0,0,0,36,1V37a1,1,0,1,0,2,0s0,0,0,0V1a1,1,0,0,0-1-1H36.91Z"
            />
            <path
              id="Line_8"
              data-name="Line 8"
              d="M42.91,9L42.78,9A1,1,0,0,0,42,10V28a1,1,0,1,0,2,0s0,0,0,0V10a1,1,0,0,0-1-1H42.91Z"
            />
            <path
              id="Line_9"
              data-name="Line 9"
              d="M48.91,15l-0.12,0A1,1,0,0,0,48,16v6a1,1,0,1,0,2,0s0,0,0,0V16a1,1,0,0,0-1-1H48.91Z"
            />
          </svg>
        </div>
        <Button
          v-if="formattedItem.status == 'waiting' && !formattedItem.isActiveCall"
          icon="check"
          rounded
          outlined
          severity="success"
          size="xs"
          @click="acceptCall"
        />
      </div>
    </div>
    <div
      v-if="formattedItem.isActiveCall"
      class="flex justify-between w-full"
    >
      <div v-if="!inlineButtons">
        <Button
          icon="material-phone_forwarded"
          plain
          severity="secondary"
          size="sm"
        />
        <!-- <Button icon="material-hearing" plain severity="secondary" size="sm" />
        <Button icon="material-school" plain severity="secondary" size="sm" /> -->
      </div>
      <div>
        <Button
          :icon="isMuted ? 'material-mic_off' : 'material-mic'"
          plain
          severity="secondary"
          size="sm"
          @click.stop="muteClick"
        />
        <Button
          v-if="formattedItem.isActiveCall"
          plain
          icon="phone-hangup"
          severity="danger"
          size="sm"
          @click.stop="endCall"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

import Icon from '@/components/icon/Icon.vue'
import Button from '@/components/button/Button.vue'

const props = defineProps({
  item: {
    type: Object,
    required: true
  },
  isSelected: {
    type: Boolean,
    default: false
  },
  isMuted: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  inlineButtons: {
    type: Boolean,
    default: false
  },
  showAudioWave: {
    type: Boolean,
    default: false
  },
  showIcon: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['accept-call', 'end-call', 'mute-click'])

const timerValue = ref(0)
const timerDisplayValue = ref('00:00')

const timerStartFrom = computed(() => {
  if (props.item.status === 'waiting') {
    return props.item.startedAt
  } else if (props.item.status === 'in-progress') {
    return props.item.connectedAt
  } else if (props.item.wrappedUpAt === null) {
    return props.item.endedAt
  } else {
    return null
  }
})

// Create a timer that updates every
setInterval(() => {
  updateTimer()
}, 1000)

function updateTimer() {
  if (timerStartFrom.value) {
    const startTime = new Date(timerStartFrom.value).getTime()
    const currentTime = new Date().getTime()
    timerValue.value = currentTime - startTime
    timerDisplayValue.value = formatTime(currentTime - startTime)
  }
}

const formattedItem = computed(() => {
  const item = props.item
  let statusMessage = ''
  let statusIconColor = ''

  let isActiveCall = null

  if (item.status === 'in-progress') {
    isActiveCall = true
  } else if (item.status === 'waiting' && item.initiatedByEntityType === 'agent') {
    isActiveCall = true
  } else {
    isActiveCall = false
  }

  if (item.status == 'waiting') {
    if (isActiveCall) {
      statusMessage = 'Active Call'
      statusIconColor = 'success'
    } else {
      statusMessage = 'Waiting'
      statusIconColor = 'secondary'
    }
  } else {
    if (item.status == 'in-progress') {
      statusMessage = 'Active Call'
      statusIconColor = 'success'
    } else if (item.status == 'completed') {
      if (item.wrappedUpAt == null) {
        statusMessage = 'Wrap-Up'
        statusIconColor = 'info'
      } else {
        statusMessage = 'Completed'
      }
    } else {
      statusMessage = 'Unknown'
      statusIconColor = 'danger'
    }
  }

  const direction = item.initiatedByEntityType === 'customer' ? 'inbound' : 'outbound'

  let icon = ''
  if (direction === 'inbound') {
    icon = 'phone-arrow-down-left'
    // icon = 'material-call_received'
    // icon = 'phone'
  } else {
    icon = 'phone-arrow-up-right'
    // icon = 'material-call_made'
    // icon = 'phone'
  }

  const isMuted = props.isMuted

  let displayTitle = null

  if (item.initiatedByEntityType === 'customer') {
    displayTitle = item.customerData?.name || item.phoneNumberDetails?.name || item.from
  } else {
    displayTitle = item.to
  }

  return {
    ...item,
    statusMessage,
    statusIconColor,
    icon,
    direction,
    isActiveCall,
    isMuted,
    displayTitle
  }
})

// Function to format ms to a human readable format. Eg: 34s or 3:23
function formatTime(ms) {
  const seconds = Math.floor(ms / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)

  // Display appropriate format based on time
  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${(minutes % 60).toString().padStart(2, '0')}:${(seconds % 60).toString().padStart(2, '0')}`
  } else if (minutes > 0) {
    return `${minutes.toString().padStart(2, '0')}:${(seconds % 60).toString().padStart(2, '0')}`
  } else {
    return `${seconds}s`
  }
}

const timerWarningThresholds = {
  warning: 60000, // 1 minute
  danger: 120000 // 2 minutes
}

const timerWarningClass = computed(() => {
  // If inbound and is waiting, show the timer
  if (formattedItem.value.status === 'waiting' && formattedItem.value.direction === 'inbound') {
    if (timerValue.value > timerWarningThresholds.warning && timerValue.value <= timerWarningThresholds.danger) {
      return 'contact-item__timer--warning'
    } else if (timerValue.value > timerWarningThresholds.danger) {
      return 'contact-item__timer--danger'
    } else {
      return ''
    }
  } else {
    return ''
  }
})

function acceptCall() {
  // emit event to parent component
  emit('accept-call', props.item)
}

function endCall() {
  // emit event to parent component
  emit('end-call', props.item)
}

function muteClick() {
  // emit event to parent component
  emit('mute-click', props.item)
}
</script>

<style scoped>
.contact-item {
  padding: var(--s-2) var(--s-3);
  margin-bottom: var(--s-1);
  border-radius: var(--rounded-lg);
  display: flex;
  gap: var(--s-1);
  white-space: nowrap;
}
.contact-item:hover {
  background-color: var(--bg-tertiary);
  cursor: pointer;
}

.contact-item.selected {
  background-color: var(--bg-tertiary);
}

.contact-item.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.contact-item__timer--warning {
  font-weight: 600;
  animation: pulseWarning 2s infinite;
}
.contact-item__timer--danger {
  font-weight: 600;
  animation: pulseError 1s infinite;
}

@keyframes pulseWarning {
  0%,
  100% {
    color: var(--text-warning);
  }
  50% {
    color: inherit;
  }
}

@keyframes pulseError {
  0%,
  100% {
    color: var(--text-error);
  }
  50% {
    color: inherit;
  }
}

.audio-wave {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 20px;
}

#wave {
  height: 25px;
  width: 25px;
  fill: var(--fg-info);
}

@keyframes pulse {
  0% {
    transform: scaleY(1);
    transform-origin: 50% 50%;
  }
  50% {
    transform: scaleY(0.7);
    transform-origin: 50% 50%;
  }
  100% {
    transform: scaleY(1);
    transform-origin: 50% 50%;
  }
}

/* CSS equivalent of the SCSS loop */
#Line_1 {
  animation: pulse 1s infinite;
  animation-delay: 0.15s;
}
#Line_2 {
  animation: pulse 1s infinite;
  animation-delay: 0.3s;
}
#Line_3 {
  animation: pulse 1s infinite;
  animation-delay: 0.45s;
}
#Line_4 {
  animation: pulse 1s infinite;
  animation-delay: 0.6s;
}
#Line_5 {
  animation: pulse 1s infinite;
  animation-delay: 0.75s;
}
#Line_6 {
  animation: pulse 1s infinite;
  animation-delay: 0.9s;
}
#Line_7 {
  animation: pulse 1s infinite;
  animation-delay: 1.05s;
}
#Line_8 {
  animation: pulse 1s infinite;
  animation-delay: 1.2s;
}
#Line_9 {
  animation: pulse 1s infinite;
  animation-delay: 1.35s;
}
</style>
